import Swiper from "swiper";
import { LoadIntersectionObserver, RequestThrottledFrame } from "../tfs-utilities";
import Analytics from "../analytics";
import { collapseComments } from "../redux/commenting/actions";

const ComicNew = {
  data: {
    comicSelector: ".js-comic-new",
    swiperNoSwipingSelector: ".js-no-swipe",
    swiperSelectorMain: ".swiper-container.js-slide-main",
    swiperSelectorExtras: ".swiper-container.js-slide-extras",
    swiperHiddenClass: "swiper-button--hidden",
    swiperNextElSelector: ".js-next",
    swiperPrevElSelector: ".js-prev",
    paginationSelector: ".js-pagination",
    paginationBulletClass: "tfs-comic-new__bullet",
    paginationBulletActiveClass: "tfs-comic-new__bullet--active",
    gtmViewEvent: "view-comic",
    gtmViewAction: "view comic",
    gtmSwipeEvent: "click",
    gtmSwipeAction: "swipe carousel",
    gtmCarouselEventSelector: "data-carousel-event",
    gtmCarouselActionSelector: "data-carousel-action"
  },

  init(store) {
    ComicNew.data.store = store;

    const slides = document.querySelectorAll(".js-slider-wrapper");
    // this is a lot, but the first one sizes the image and caption
    // and the second corrects when the caption shifts things slightly
    // at least we put it in an RAF so it's not quite as bad
    // TODO: obviously that isn't great, fix it
    ComicNew.sizeItems(slides);
    requestAnimationFrame(() => {
      ComicNew.sizeItems(slides);
    });
    ComicNew.watchForResize(slides);

    ComicNew.observeComics();

    const swiperContainer = document.querySelector(this.data.swiperSelectorMain);
    if (swiperContainer) {
      ComicNew.runSwiper(swiperContainer.dataset.selectedIndex);
      ComicNew.trackClicks();
    }
  },

  runSwiper(selectedIndex) {
    const swiper = new Swiper(this.data.swiperSelectorMain, {
      init: false,
      initialSlide: selectedIndex,
      noSwipingSelector: this.data.swiperNoSwipingSelector,
      // Responsive breakpoints
      breakpoints: {
        // when window width is >= 0px
        0: {
          autoHeight: true,
          observer: true,
          observeParents: true,
          observeSlideChildren: true
        },
        // when window width is >= 768px (md)
        768: {
          autoHeight: false
        }
      },
      a11y: {
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide"
      },
      pagination: {
        el: this.data.paginationSelector,
        type: "bullets",
        clickable: true,
        bulletClass: this.data.paginationBulletClass,
        bulletActiveClass: this.data.paginationBulletActiveClass
      },
      navigation: {
        hideOnClick: false,
        hiddenClass: this.data.swiperHiddenClass,
        nextEl: this.data.swiperNextElSelector,
        prevEl: this.data.swiperPrevElSelector,
        disabledClass: "disabled"
      }
    });
    const swiperExtras = new Swiper(this.data.swiperSelectorExtras, {
      init: false,
      initialSlide: selectedIndex,
      noSwipingSelector: this.data.swiperNoSwipingSelector
    });

    // The carousel is almost entirely event driven
    swiper.on("init", () => {
      ComicNew.disableSwipe(swiper);
    });

    swiper.on("slideChange", () => {
      // get the path for the newly selected slide
      const { path } = swiper.slides[swiper.activeIndex].dataset;
      const currentState = window.history.state;

      if (currentState && currentState.path === path) {
        // somehow haven't actually gone anywhere, nevermind
        return;
      }
      ComicNew.handleSlideChange(swiper, swiperExtras, path);
    });

    swiper.on("touchEnd", () => {
      ComicNew.sendGTMEvent(this.data.gtmSwipeEvent, this.data.gtmSwipeAction, window.location.href);
    });

    window.addEventListener("popstate", event => {
      // respond to forward/back navigation
      swiper.slideTo(event.state.index);
      swiperExtras.slideTo(event.state.index);
    });

    swiper.init();
    swiperExtras.init();
    return swiper;
  },

  handleSlideChange(swiper, swiperExtras, path) {
    // update the path to match that slide
    // Analytics Note: GTM is configured to automatically track history state updates
    window.history.pushState({ path, index: swiper.activeIndex }, null, path);

    // move the extras to the same slide
    swiperExtras.slideTo(swiper.activeIndex);

    // unconditionally collapse all comments in the slider to reset state
    ComicNew.data.store.dispatch(collapseComments());
  },

  disableSwipe(swiper) {
    if (swiper.slides.length === 1) {
      /* eslint-disable no-param-reassign */
      // override the swiping setting if there's only 1 slide and nowhere to swipe to
      swiper.allowTouchMove = false;
      /* eslint-enable no-param-reassign */
    }
  },

  trackClicks() {
    const carouselClickers = document.querySelectorAll(`[${ComicNew.data.gtmCarouselEventSelector}]`);

    if (carouselClickers) {
      carouselClickers.forEach(element => {
        const clickable = element;

        clickable.onclick = function didClick() {
          const event = clickable.getAttribute(`${ComicNew.data.gtmCarouselEventSelector}`);
          const action = clickable.getAttribute(`${ComicNew.data.gtmCarouselActionSelector}`);

          ComicNew.sendGTMEvent(event, action, window.location.href);
        };
      });
    }
  },

  observeComics() {
    const comics = document.querySelectorAll(ComicNew.data.comicSelector);
    LoadIntersectionObserver().then(() => {
      ComicNew.createObserver(comics);
    });
  },

  watchForResize(slides) {
    RequestThrottledFrame("resize", "optimizedResize");
    window.addEventListener(
      "optimizedResize",
      () => {
        ComicNew.sizeItems(slides);
      },
      false
    );
  },

  sizeItems(slides) {
    Array.prototype.forEach.call(slides, slide => {
      const slideElement = slide;
      slideElement.style.opacity = 0;

      const image = slide.querySelector(".js-slider-image");
      const { parentElement } = image;
      parentElement.style.height = "100%";

      const parentWidth = parentElement.clientWidth;
      const parentHeight = parentElement.clientHeight;

      const imageWidth = image.dataset.width;
      const imageHeight = image.dataset.height;

      const ratio = imageHeight / imageWidth;

      let newWidth = 0;
      let newHeight = 0;
      if (parentWidth * ratio > parentHeight) {
        newWidth = Math.min(parentWidth, Math.ceil(parentHeight / ratio));
        newHeight = Math.floor(newWidth * ratio);
      } else {
        newWidth = parentWidth;
        newHeight = Math.floor(parentWidth * ratio);
      }

      image.style.width = `${newWidth}px`;
      image.style.height = `${newHeight}px`;
      parentElement.style.height = `${newHeight}px`;

      const caption = slide.querySelector(".js-slider-caption");
      if (caption) {
        if (image.offsetWidth === 0) {
          // something happened and the width didn't calculate correctly, fall back on normal styles
          return;
        }
        caption.style.width = `${image.offsetWidth}px`;
      }

      slideElement.style.opacity = 1;
    });
  },

  createObserver(targets) {
    const config = {
      threshold: 1
    };

    const observer = new IntersectionObserver((entries, self) => {
      Array.prototype.forEach.call(entries, entry => {
        if (entry.isIntersecting) {
          ComicNew.sendGTMEvent(ComicNew.data.gtmViewEvent, ComicNew.data.gtmViewAction, window.location.href);
          self.unobserve(entry.target);
        }
      });
    }, config);

    // Watch for images to come into view
    Array.prototype.forEach.call(targets, image => observer.observe(image));
  },

  sendGTMEvent(event, action, label) {
    Analytics.track(event, {
      action,
      label
    });
  }
};
export default ComicNew;
